import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, X, Loader2, Upload, ExternalLink, Truck, FileText, ChevronRight, Receipt, BarChart3, Users, ShoppingBag, Wallet, Search } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAdmin } from "@/context/AdminContext";
import { fetchProducts, createProduct, updateProduct, deleteProduct, uploadImage, listOrders, generateAwb, getAwbLabel, generateInvoice, getStats, updateOrderStatus, fmtPrice } from "@/lib/api";

const SCENTS = [
  { value: "lavanda", label: "Lavandă" },
  { value: "gramur", label: "Gramur" },
  { value: "ocean", label: "Ocean" },
  { value: "neutru", label: "Neutru" },
];
const CATEGORIES = [
  { value: "detergent", label: "Detergent" },
  { value: "degresant", label: "Degresant" },
  { value: "balsam", label: "Balsam" },
];

const empty = {
  name: "", tagline: "", description: "", price: "", old_price: "",
  scent: "neutru", scent_label: "", category: "detergent", volume: "",
  image: "", bestseller: false, featured: false, notes: "",
  weight: "11", length: "20", width: "20", height: "30",
};

export default function Admin() {
  const { token, logout } = useAdmin();
  const navigate = useNavigate();
  const [tab, setTab] = useState("statistici");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [orderSearch, setOrderSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkInvoiceBusy, setBulkInvoiceBusy] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [awbForm, setAwbForm] = useState(null);
  const [awbBusy, setAwbBusy] = useState(false);
  const [labelBusy, setLabelBusy] = useState(false);
  const [invoiceBusy, setInvoiceBusy] = useState(false);

  const load = () => fetchProducts().then(setProducts).catch(() => {});
  const loadOrders = () => listOrders(token).then(setOrders).catch(() => toast.error("Nu am putut încărca comenzile"));
  const loadStats = () => {
    setStatsLoading(true);
    getStats(token).then(setStats).catch(() => toast.error("Nu am putut încărca statisticile")).finally(() => setStatsLoading(false));
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tab === "comenzi") loadOrders();
    if (tab === "statistici") loadStats();
  }, [tab, token]);

  const filteredOrders = orders.filter((o) => {
    const q = orderSearch.trim().toLowerCase();
    if (!q) return true;
    return (o.customer_name || "").toLowerCase().includes(q)
      || (o.order_number || "").toLowerCase().includes(q)
      || (o.phone || "").toLowerCase().includes(q)
      || (o.city || "").toLowerCase().includes(q);
  });

  const selectableOrders = filteredOrders;
  const toggleSelect = (num) =>
    setSelectedIds((s) => (s.includes(num) ? s.filter((x) => x !== num) : [...s, num]));
  const toggleSelectAll = () =>
    setSelectedIds((s) => (s.length === selectableOrders.length ? [] : selectableOrders.map((o) => o.order_number)));

  const awbPayloadFromOrder = (o) => ({
    customer_name: o.customer_name || "", phone: o.phone || "", email: o.email || "",
    address: o.address || "", city: o.city || "", county: o.county || "", postal_code: o.postal_code || "",
    total_weight: 1, parcels: 1, cash_repayment: o.total ?? 0,
    package_content: "Detergenti", observations: "",
  });

  // Colete calculate automat: o bucată dintr-un produs = un colet cu dimensiunile/greutatea produsului
  const coleteFromOrder = (o) => {
    const list = [];
    (o?.items || []).forEach((it) => {
      const p = products.find((x) => x.id === it.product_id) || {};
      const qty = Math.max(1, it.quantity || 1);
      for (let i = 0; i < qty; i++) {
        list.push({
          name: it.name,
          weight: p.weight ?? 11, length: p.length ?? 20, width: p.width ?? 20, height: p.height ?? 30,
        });
      }
    });
    const totalWeight = list.reduce((s, c) => s + (c.weight || 0), 0);
    return { list, totalWeight };
  };

  const doBulkAwb = async () => {
    const targets = orders.filter((o) => selectedIds.includes(o.order_number) && !o.awb);
    if (targets.length === 0) return;
    setBulkBusy(true);
    let ok = 0; const fails = [];
    for (const o of targets) {
      try {
        await generateAwb(token, o.order_number, awbPayloadFromOrder(o));
        ok += 1;
      } catch (err) {
        fails.push(`${o.order_number}: ${err?.response?.data?.detail || "eroare"}`);
      }
    }
    setBulkBusy(false);
    setSelectedIds([]);
    await loadOrders();
    if (ok) toast.success(`${ok} AWB generate cu succes`);
    if (fails.length) toast.error(`${fails.length} au eșuat`, { description: fails.slice(0, 3).join(" · ") });
  };

  const doBulkInvoice = async () => {
    const targets = orders.filter((o) => selectedIds.includes(o.order_number) && !o.invoice_number);
    if (targets.length === 0) { toast.info("Comenzile selectate au deja factură"); return; }
    setBulkInvoiceBusy(true);
    let ok = 0; const fails = [];
    for (const o of targets) {
      try {
        await generateInvoice(token, o.order_number);
        ok += 1;
      } catch (err) {
        fails.push(`${o.order_number}: ${err?.response?.data?.detail || "eroare"}`);
      }
    }
    setBulkInvoiceBusy(false);
    setSelectedIds([]);
    await loadOrders();
    if (ok) toast.success(`${ok} facturi emise cu succes`);
    if (fails.length) toast.error(`${fails.length} au eșuat`, { description: fails.slice(0, 3).join(" · ") });
  };

  const changeStatus = async (status) => {
    setStatusBusy(true);
    try {
      await updateOrderStatus(token, selectedOrder.order_number, status);
      setSelectedOrder((o) => ({ ...o, status }));
      setOrders((list) => list.map((o) => (o.order_number === selectedOrder.order_number ? { ...o, status } : o)));
      toast.success("Status actualizat");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nu am putut actualiza statusul");
    } finally {
      setStatusBusy(false);
    }
  };

  const openOrder = (o) => {
    setSelectedOrder(o);
    setAwbForm({
      customer_name: o.customer_name || "", phone: o.phone || "", email: o.email || "",
      address: o.address || "", city: o.city || "", county: o.county || "", postal_code: o.postal_code || "",
      total_weight: "1", parcels: "1", cash_repayment: String(o.total ?? 0),
      package_content: "Detergenti", observations: "",
    });
  };

  const changeAwb = (k) => (e) => setAwbForm((f) => ({ ...f, [k]: e.target.value }));

  const doGenerateAwb = async () => {
    if (!awbForm.customer_name || !awbForm.phone || !awbForm.address || !awbForm.city || !awbForm.county) {
      toast.error("Nume, telefon, adresă, localitate și județ sunt obligatorii");
      return;
    }
    setAwbBusy(true);
    try {
      const payload = {
        customer_name: awbForm.customer_name, phone: awbForm.phone, email: awbForm.email,
        address: awbForm.address, city: awbForm.city, county: awbForm.county, postal_code: awbForm.postal_code,
        total_weight: parseFloat(awbForm.total_weight) || 1,
        parcels: parseInt(awbForm.parcels) || 1,
        cash_repayment: parseFloat(awbForm.cash_repayment) || 0,
        package_content: awbForm.package_content || "Detergenti",
        observations: awbForm.observations || "",
      };
      const res = await generateAwb(token, selectedOrder.order_number, payload);
      toast.success(`AWB generat: ${res.awb}`);
      setSelectedOrder((o) => ({ ...o, awb: res.awb, status: "expediata", ...payload }));
      loadOrders();
    } catch (err) {
      if (err?.response?.status === 401) { toast.error("Sesiune expirată"); logout(); }
      else toast.error(err?.response?.data?.detail || "Generarea AWB a eșuat");
    } finally {
      setAwbBusy(false);
    }
  };

  const downloadLabel = async () => {
    setLabelBusy(true);
    try {
      const blob = await getAwbLabel(token, selectedOrder.order_number);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error("Nu am putut obține eticheta PDF");
    } finally {
      setLabelBusy(false);
    }
  };

  const doGenerateInvoice = async () => {
    setInvoiceBusy(true);
    try {
      const res = await generateInvoice(token, selectedOrder.order_number);
      toast.success(`Factură emisă: ${res.invoice_series}-${res.invoice_number}`);
      setSelectedOrder((o) => ({ ...o, invoice_series: res.invoice_series, invoice_number: res.invoice_number, invoice_link: res.invoice_link }));
      loadOrders();
    } catch (err) {
      if (err?.response?.status === 401) { toast.error("Sesiune expirată"); logout(); }
      else toast.error(err?.response?.data?.detail || "Emiterea facturii a eșuat");
    } finally {
      setInvoiceBusy(false);
    }
  };

  const openAdd = () => { setEditing(null); setForm(empty); setFormOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "", tagline: p.tagline || "", description: p.description || "",
      price: String(p.price ?? ""), old_price: p.old_price != null ? String(p.old_price) : "",
      scent: p.scent || "neutru", scent_label: p.scent_label || "", category: p.category || "detergent",
      volume: p.volume || "", image: p.image || "", bestseller: !!p.bestseller, featured: !!p.featured,
      notes: (p.notes || []).join(", "),
      weight: String(p.weight ?? "11"), length: String(p.length ?? "20"),
      width: String(p.width ?? "20"), height: String(p.height ?? "30"),
    });
    setFormOpen(true);
  };

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => setForm((f) => ({ ...f, [k]: !f[k] }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadImage(token, file);
      setForm((f) => ({ ...f, image: res.absoluteUrl }));
      toast.success("Imagine încărcată");
    } catch (err) {
      toast.error("Încărcarea imaginii a eșuat");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) { toast.error("Nume și preț sunt obligatorii"); return; }
    setSaving(true);
    const payload = {
      name: form.name, tagline: form.tagline, description: form.description,
      price: parseFloat(form.price), old_price: form.old_price ? parseFloat(form.old_price) : null,
      scent: form.scent, scent_label: form.scent_label, category: form.category, volume: form.volume,
      image: form.image, bestseller: form.bestseller, featured: form.featured,
      notes: form.notes.split(",").map((s) => s.trim()).filter(Boolean),
      weight: parseFloat(form.weight) || 11, length: parseInt(form.length) || 20,
      width: parseInt(form.width) || 20, height: parseInt(form.height) || 30,
    };
    try {
      if (editing) { await updateProduct(token, editing.id, payload); toast.success("Produs actualizat"); }
      else { await createProduct(token, payload); toast.success("Produs adăugat"); }
      setFormOpen(false);
      load();
    } catch (err) {
      if (err?.response?.status === 401) { toast.error("Sesiune expirată"); logout(); }
      else toast.error("Salvarea a eșuat");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteProduct(token, pendingDelete.id);
      toast.success("Produs șters");
      setPendingDelete(null);
      load();
    } catch (err) {
      toast.error("Ștergerea a eșuat");
    }
  };

  return (
    <main className="min-h-screen pb-24" data-testid="admin-dashboard">
      {/* Top bar */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-[#EDEAE3]/85 border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div>
            <p className="font-serif text-2xl leading-none">Panou Admin</p>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A4844] mt-1">deter-ecobox.ro</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="text-xs tracking-widest uppercase text-[#4A4844] hover:text-[#0E0F12] transition-colors hidden sm:inline">Vezi magazinul</button>
            <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="inline-flex items-center gap-2 px-5 py-2.5 border border-black/15 rounded-sm text-xs tracking-widest uppercase hover:bg-[#0E0F12] hover:text-[#EDEAE3] transition-colors">
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> Ieșire
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-10">
        {/* Tabs */}
        <div className="flex items-center gap-3 mb-8">
          {[["statistici", "Statistici"], ["produse", "Produse"], ["comenzi", "Comenzi"]].map(([k, l]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
              className={`px-5 py-2.5 text-xs tracking-widest uppercase rounded-full border transition-colors ${tab === k ? "bg-[#0E0F12] text-[#EDEAE3] border-[#0E0F12]" : "border-black/12 text-[#4A4844] hover:text-[#0E0F12]"}`}>
              {l}
            </button>
          ))}
          {tab === "produse" && (
            <button data-testid="admin-add-product" onClick={openAdd} className="ml-auto inline-flex items-center gap-2 px-6 py-3 bg-[#0E0F12] text-[#EDEAE3] text-xs tracking-widest uppercase rounded-sm hover:-translate-y-[2px] transition-transform">
              <Plus className="w-4 h-4" strokeWidth={1.6} /> Adaugă produs
            </button>
          )}
        </div>

        {/* Statistici */}
        {tab === "statistici" && (
          <div data-testid="stats-panel">
            {statsLoading && <p className="text-center py-16 text-[#4A4844]">Se încarcă…</p>}
            {stats && (
              <div className="space-y-8">
                {/* KPI cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard icon={Wallet} label="Venit net" value={fmtPrice(stats.revenue_total)} sub={`Luna: ${fmtPrice(stats.revenue_month)}${stats.returns_total ? ` · Retururi: -${fmtPrice(stats.returns_total)}` : ""}`} testid="stat-revenue-total" />
                  <StatCard icon={ShoppingBag} label="Comenzi valide" value={stats.orders_total} sub={`Azi: ${stats.orders_today} · Luna: ${stats.orders_month}${stats.returns_count ? ` · Retur: ${stats.returns_count}` : ""}`} testid="stat-orders-total" />
                  <StatCard icon={Users} label="Vizitatori total" value={stats.visitors_total} sub={`Azi: ${stats.visitors_today} · Luna: ${stats.visitors_month}`} testid="stat-visitors-total" />
                  <StatCard icon={BarChart3} label="Valoare medie comandă" value={fmtPrice(stats.avg_order_value)} sub={`Venit azi: ${fmtPrice(stats.revenue_today)}`} testid="stat-aov" />
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Surse trafic */}
                  <div className="border border-black/10 rounded-md p-6 bg-white/40">
                    <p className="text-[11px] tracking-[0.3em] uppercase text-[#4A4844] mb-5">De unde vin vizitatorii</p>
                    {stats.visitors_by_source.length === 0 && <p className="text-sm text-[#4A4844]">Încă nu sunt date de trafic.</p>}
                    <div className="space-y-3">
                      {stats.visitors_by_source.map((s) => {
                        const max = Math.max(...stats.visitors_by_source.map((x) => x.count), 1);
                        return (
                          <div key={s.source} data-testid={`source-${s.source}`}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-[#0E0F12]">{s.source}</span>
                              <span className="text-[#4A4844]">{s.count}</span>
                            </div>
                            <div className="h-2 rounded-full bg-black/8 overflow-hidden">
                              <div className="h-full bg-gramur rounded-full" style={{ width: `${(s.count / max) * 100}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Trafic & comenzi 7 zile */}
                  <div className="border border-black/10 rounded-md p-6 bg-white/40">
                    <p className="text-[11px] tracking-[0.3em] uppercase text-[#4A4844] mb-5">Ultimele 7 zile</p>
                    <div className="flex items-end justify-between gap-2 h-40">
                      {stats.visits_daily.map((d, i) => {
                        const maxV = Math.max(...stats.visits_daily.map((x) => x.count), 1);
                        const ord = stats.orders_daily[i]?.count || 0;
                        const maxO = Math.max(...stats.orders_daily.map((x) => x.count), 1);
                        return (
                          <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                            <div className="w-full flex items-end justify-center gap-1 h-32">
                              <div className="w-1/2 bg-[#0E0F12] rounded-t-sm" style={{ height: `${(d.count / maxV) * 100}%` }} title={`${d.count} vizite`} />
                              <div className="w-1/2 bg-gramur rounded-t-sm" style={{ height: `${(ord / maxO) * 100}%` }} title={`${ord} comenzi`} />
                            </div>
                            <span className="text-[9px] text-[#4A4844]">{d.day.slice(5)}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-4 mt-4 text-[11px] text-[#4A4844]">
                      <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#0E0F12]" /> Vizite</span>
                      <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-gramur" /> Comenzi</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Products table */}
        {tab === "produse" && (
          <div className="border border-black/10 rounded-md overflow-hidden">
            <div className="hidden md:grid grid-cols-[64px_1fr_140px_120px_120px_100px] gap-4 px-5 py-3 bg-[#E6E1D8] text-[10px] tracking-[0.2em] uppercase text-[#4A4844]">
              <span>Imagine</span><span>Nume</span><span>Categorie</span><span>Preț</span><span>Best seller</span><span>Acțiuni</span>
            </div>
            {products.map((p) => (
              <div key={p.id} data-testid={`product-row-${p.id}`} className="grid grid-cols-[64px_1fr] md:grid-cols-[64px_1fr_140px_120px_120px_100px] gap-4 items-center px-5 py-4 border-t border-black/8">
                <div className="w-14 h-14 rounded-sm overflow-hidden bg-[#E6E1D8] border border-black/10">
                  {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover" /> : null}
                </div>
                <div>
                  <p className="font-serif text-lg leading-tight">{p.name}</p>
                  <p className="text-xs text-[#4A4844]">{p.scent_label}</p>
                </div>
                <span className="hidden md:block text-sm text-[#4A4844] capitalize">{p.category}</span>
                <span className="hidden md:block text-sm">{fmtPrice(p.price)}</span>
                <span className="hidden md:block text-sm">{p.bestseller ? "Da" : "—"}</span>
                <div className="col-span-2 md:col-span-1 flex items-center gap-2 md:justify-start">
                  <button data-testid={`edit-${p.id}`} onClick={() => openEdit(p)} className="w-9 h-9 flex items-center justify-center border border-black/12 rounded-sm hover:bg-[#0E0F12] hover:text-[#EDEAE3] transition-colors">
                    <Pencil className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <button data-testid={`delete-${p.id}`} onClick={() => setPendingDelete(p)} className="w-9 h-9 flex items-center justify-center border border-black/12 rounded-sm hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors">
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
            {products.length === 0 && <p className="px-5 py-10 text-center text-[#4A4844]">Niciun produs.</p>}
          </div>
        )}

        {/* Orders */}
        {tab === "comenzi" && (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A4844]" strokeWidth={1.6} />
                <input
                  data-testid="order-search"
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                  placeholder="Caută după nume, nr. comandă, telefon, oraș…"
                  className="w-full pl-11 pr-4 py-3 bg-white/60 border border-black/12 rounded-sm text-sm outline-none focus:border-gramur transition-colors"
                />
              </div>
              {selectedIds.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <button data-testid="bulk-awb-btn" onClick={doBulkAwb} disabled={bulkBusy || bulkInvoiceBusy}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#0E0F12] text-[#EDEAE3] text-xs tracking-widest uppercase rounded-sm hover:-translate-y-[2px] transition-transform disabled:opacity-60">
                    {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" strokeWidth={1.6} />}
                    {bulkBusy ? "Se generează…" : `Generează AWB (${selectedIds.length})`}
                  </button>
                  <button data-testid="bulk-invoice-btn" onClick={doBulkInvoice} disabled={bulkBusy || bulkInvoiceBusy}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-black/20 text-[#0E0F12] text-xs tracking-widest uppercase rounded-sm hover:bg-[#0E0F12] hover:text-[#EDEAE3] transition-colors disabled:opacity-60">
                    {bulkInvoiceBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" strokeWidth={1.6} />}
                    {bulkInvoiceBusy ? "Se emit…" : `Emite facturi (${selectedIds.length})`}
                  </button>
                </div>
              )}
            </div>
            <div className="border border-black/10 rounded-md overflow-hidden" data-testid="orders-list">
              <div className="hidden md:grid grid-cols-[36px_140px_1fr_120px_90px_90px_140px_28px] gap-3 px-5 py-3 bg-[#E6E1D8] text-[10px] tracking-[0.2em] uppercase text-[#4A4844] items-center">
                <input type="checkbox" data-testid="select-all-orders" checked={selectableOrders.length > 0 && selectedIds.length === selectableOrders.length} onChange={toggleSelectAll} className="w-4 h-4 accent-[#0E0F12]" />
                <span>Comandă</span><span>Client</span><span>Status</span><span>Buc.</span><span>Total</span><span>AWB</span><span></span>
              </div>
              {filteredOrders.map((o) => (
                <div key={o.id} data-testid={`order-row-${o.order_number}`} onClick={() => openOrder(o)}
                  className="grid grid-cols-[36px_1fr_28px] md:grid-cols-[36px_140px_1fr_120px_90px_90px_140px_28px] gap-2 md:gap-3 px-5 py-4 border-t border-black/8 text-sm items-center cursor-pointer hover:bg-black/[0.03] transition-colors">
                  <div onClick={(e) => e.stopPropagation()} className="flex items-center">
                    <input type="checkbox" data-testid={`select-${o.order_number}`} checked={selectedIds.includes(o.order_number)} onChange={() => toggleSelect(o.order_number)} className="w-4 h-4 accent-[#0E0F12]" />
                  </div>
                  <span className="font-mono text-gramur">{o.order_number}</span>
                  <span className="truncate">{o.customer_name} · {o.city}</span>
                  <span><StatusBadge status={o.status} /></span>
                  <span className="hidden md:block text-[#4A4844]">{o.items.reduce((s, i) => s + i.quantity, 0)}</span>
                  <span className="hidden md:block">{fmtPrice(o.total)}</span>
                  <span className="hidden md:block">
                    {o.awb
                      ? <span className="inline-flex items-center gap-1.5 text-xs font-mono text-green-700"><Truck className="w-3.5 h-3.5" strokeWidth={1.6} />{o.awb}</span>
                      : <span className="text-[11px] tracking-wider uppercase text-[#4A4844]/70">Fără AWB</span>}
                  </span>
                  <ChevronRight className="hidden md:block w-4 h-4 text-[#4A4844] justify-self-end" strokeWidth={1.5} />
                </div>
              ))}
              {filteredOrders.length === 0 && <p className="px-5 py-10 text-center text-[#4A4844]">{orders.length === 0 ? "Nicio comandă încă." : "Nicio comandă găsită."}</p>}
            </div>
          </div>
        )}
      </div>

      {/* Order detail + AWB */}
      <Dialog open={!!selectedOrder} onOpenChange={(o) => !o && setSelectedOrder(null)}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto bg-[#EDEAE3] border border-black/10 rounded-md [&>button]:hidden" data-testid="order-detail">
          {selectedOrder && awbForm && (
            <>
              <DialogTitle className="font-serif text-3xl font-light">Comandă {selectedOrder.order_number}</DialogTitle>
              <DialogDescription className="sr-only">Detalii comandă și generare AWB</DialogDescription>
              <button onClick={() => setSelectedOrder(null)} className="absolute top-5 right-5 w-9 h-9 rounded-full border border-black/12 flex items-center justify-center text-[#4A4844] hover:text-[#0E0F12]">
                <X className="w-4 h-4" />
              </button>

              {/* Items summary */}
              <div className="mt-2 border border-black/10 rounded-sm divide-y divide-black/8">
                {selectedOrder.items.map((it, i) => (
                  <div key={i} className="flex justify-between px-4 py-2.5 text-sm">
                    <span>{it.name} <span className="text-[#4A4844]">× {it.quantity}</span></span>
                    <span>{fmtPrice(it.price * it.quantity)}</span>
                  </div>
                ))}
                <div className="flex justify-between px-4 py-2.5 text-sm font-medium bg-[#E6E1D8]">
                  <span>Total</span><span>{fmtPrice(selectedOrder.total)}</span>
                </div>
              </div>

              {/* Status comandă */}
              <div className="mt-5">
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#4A4844] mb-3">Status comandă</p>
                <div className="flex flex-wrap gap-2" data-testid="status-selector">
                  {[["noua", "Nouă"], ["confirmata", "Confirmată"], ["expediata", "Expediată"], ["anulata", "Anulată"], ["returnata", "Returnată"]].map(([val, label]) => {
                    const active = (selectedOrder.status || "noua") === val || (val === "noua" && selectedOrder.status === "primita");
                    return (
                      <button key={val} data-testid={`status-${val}`} disabled={statusBusy} onClick={() => changeStatus(val)}
                        className={`px-4 py-2.5 text-xs tracking-wider uppercase rounded-sm border transition-colors disabled:opacity-60 ${active ? "bg-[#0E0F12] text-[#EDEAE3] border-[#0E0F12]" : "border-black/15 text-[#4A4844] hover:border-gramur hover:text-[#0E0F12]"}`}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {selectedOrder.awb ? (
                <div className="mt-5 p-5 border border-green-600/30 bg-green-600/5 rounded-sm" data-testid="awb-generated">
                  <p className="text-[11px] tracking-[0.2em] uppercase text-[#4A4844]">AWB Cargus</p>
                  <p className="font-mono text-2xl text-green-800 mt-1">{selectedOrder.awb}</p>
                  <button data-testid="download-label-btn" onClick={downloadLabel} disabled={labelBusy}
                    className="mt-4 inline-flex items-center gap-2 px-5 py-3 bg-[#0E0F12] text-[#EDEAE3] text-xs tracking-widest uppercase rounded-sm hover:-translate-y-[2px] transition-transform disabled:opacity-60">
                    {labelBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" strokeWidth={1.6} />}
                    Descarcă eticheta PDF
                  </button>
                </div>
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); doGenerateAwb(); }} className="mt-5 space-y-4">
                  <p className="text-[11px] tracking-[0.2em] uppercase text-[#4A4844]">Date livrare (editabile)</p>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Nume destinatar *" testid="awb-name" value={awbForm.customer_name} onChange={changeAwb("customer_name")} required />
                    <Field label="Telefon *" testid="awb-phone" value={awbForm.phone} onChange={changeAwb("phone")} required />
                  </div>
                  <Field label="Email" testid="awb-email" value={awbForm.email} onChange={changeAwb("email")} />
                  <Field label="Adresă (stradă, nr., bloc, ap.) *" testid="awb-address" value={awbForm.address} onChange={changeAwb("address")} required />
                  <div className="grid grid-cols-3 gap-4">
                    <Field label="Localitate *" testid="awb-city" value={awbForm.city} onChange={changeAwb("city")} required />
                    <Field label="Județ *" testid="awb-county" value={awbForm.county} onChange={changeAwb("county")} required />
                    <Field label="Cod poștal" testid="awb-postal" value={awbForm.postal_code} onChange={changeAwb("postal_code")} />
                  </div>

                  <p className="text-[11px] tracking-[0.2em] uppercase text-[#4A4844] pt-2">Colete (calculate automat)</p>
                  {(() => {
                    const { list, totalWeight } = coleteFromOrder(selectedOrder);
                    return (
                      <div data-testid="awb-colete-summary" className="rounded-sm border border-black/10 bg-black/[0.02] p-3 text-sm">
                        <div className="flex justify-between font-medium">
                          <span>{list.length} {list.length === 1 ? "colet" : "colete"}</span>
                          <span>{totalWeight} kg total</span>
                        </div>
                        <ul className="mt-2 space-y-1 text-xs text-[#4A4844]">
                          {list.map((c, i) => (
                            <li key={i} className="flex justify-between gap-2">
                              <span className="truncate">{c.name}</span>
                              <span className="whitespace-nowrap font-mono">{c.length}×{c.width}×{c.height} cm · {c.weight} kg</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Ramburs (lei)" testid="awb-cod" type="number" step="0.01" value={awbForm.cash_repayment} onChange={changeAwb("cash_repayment")} />
                    <Field label="Conținut colet" testid="awb-content" value={awbForm.package_content} onChange={changeAwb("package_content")} />
                  </div>
                  <Field label="Observații curier" testid="awb-observations" value={awbForm.observations} onChange={changeAwb("observations")} />

                  <button type="submit" data-testid="generate-awb-btn" disabled={awbBusy}
                    className="w-full mt-2 inline-flex items-center justify-center gap-2 py-3.5 bg-[#0E0F12] text-[#EDEAE3] rounded-sm text-sm tracking-widest uppercase hover:-translate-y-[2px] transition-transform disabled:opacity-60">
                    {awbBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" strokeWidth={1.6} />}
                    {awbBusy ? "Se generează AWB…" : "Generează AWB Cargus"}
                  </button>
                </form>
              )}

              {/* Factură Oblio */}
              <div className="mt-5 pt-5 border-t border-black/10">
                {selectedOrder.invoice_number ? (
                  <div className="p-5 border border-gramur/40 bg-gramur/5 rounded-sm" data-testid="invoice-generated">
                    <p className="text-[11px] tracking-[0.2em] uppercase text-[#4A4844]">Factură Oblio</p>
                    <p className="font-mono text-2xl mt-1">{selectedOrder.invoice_series}-{selectedOrder.invoice_number}</p>
                    {selectedOrder.invoice_link && (
                      <a data-testid="invoice-link" href={selectedOrder.invoice_link} target="_blank" rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center gap-2 px-5 py-3 bg-[#0E0F12] text-[#EDEAE3] text-xs tracking-widest uppercase rounded-sm hover:-translate-y-[2px] transition-transform">
                        <FileText className="w-4 h-4" strokeWidth={1.6} /> Deschide factura PDF
                      </a>
                    )}
                  </div>
                ) : (
                  <button data-testid="generate-invoice-btn" onClick={doGenerateInvoice} disabled={invoiceBusy}
                    className="w-full inline-flex items-center justify-center gap-2 py-3.5 border border-black/20 rounded-sm text-sm tracking-widest uppercase hover:bg-[#0E0F12] hover:text-[#EDEAE3] transition-colors disabled:opacity-60">
                    {invoiceBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" strokeWidth={1.6} />}
                    {invoiceBusy ? "Se emite factura…" : "Emite factură Oblio"}
                  </button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Product form */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto bg-[#EDEAE3] border border-black/10 rounded-md [&>button]:hidden" data-testid="admin-product-form">
          <DialogTitle className="font-serif text-3xl font-light">{editing ? "Editează produs" : "Adaugă produs"}</DialogTitle>
          <DialogDescription className="sr-only">Formular produs</DialogDescription>
          <button onClick={() => setFormOpen(false)} className="absolute top-5 right-5 w-9 h-9 rounded-full border border-black/12 flex items-center justify-center text-[#4A4844] hover:text-[#0E0F12]">
            <X className="w-4 h-4" />
          </button>

          <form onSubmit={save} className="space-y-4 mt-2">
            <Field label="Nume *" testid="pf-name" value={form.name} onChange={change("name")} required />
            <Field label="Subtitlu (tagline)" testid="pf-tagline" value={form.tagline} onChange={change("tagline")} />
            <div>
              <Lbl>Descriere</Lbl>
              <textarea data-testid="pf-description" value={form.description} onChange={change("description")} rows={3}
                className="w-full bg-transparent border border-black/15 rounded-sm focus:border-gramur outline-none p-3 text-sm transition-colors" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Preț (lei) *" testid="pf-price" type="number" step="0.01" value={form.price} onChange={change("price")} required />
              <Field label="Preț vechi (opțional)" testid="pf-old-price" type="number" step="0.01" value={form.old_price} onChange={change("old_price")} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Lbl>Aromă</Lbl>
                <select data-testid="pf-scent" value={form.scent} onChange={change("scent")} className="w-full bg-transparent border border-black/15 rounded-sm outline-none p-3 text-sm">
                  {SCENTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <Lbl>Categorie</Lbl>
                <select data-testid="pf-category" value={form.category} onChange={change("category")} className="w-full bg-transparent border border-black/15 rounded-sm outline-none p-3 text-sm">
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Etichetă aromă" testid="pf-scent-label" value={form.scent_label} onChange={change("scent_label")} />
              <Field label="Volum" testid="pf-volume" value={form.volume} onChange={change("volume")} />
            </div>
            <Field label="Note olfactive (separate prin virgulă)" testid="pf-notes" value={form.notes} onChange={change("notes")} />

            {/* Colet pentru AWB Cargus */}
            <div className="pt-1">
              <Lbl>Colet (AWB Cargus) — o bucată = un colet</Lbl>
              <div className="grid grid-cols-4 gap-3 mt-2">
                <Field label="Greutate (kg)" testid="pf-weight" type="number" step="0.1" value={form.weight} onChange={change("weight")} />
                <Field label="Lungime (cm)" testid="pf-length" type="number" value={form.length} onChange={change("length")} />
                <Field label="Lățime (cm)" testid="pf-width" type="number" value={form.width} onChange={change("width")} />
                <Field label="Înălțime (cm)" testid="pf-height" type="number" value={form.height} onChange={change("height")} />
              </div>
            </div>

            {/* Image upload */}
            <div>
              <Lbl>Imagine produs</Lbl>
              <div className="flex items-center gap-4">
                <div className="w-20 h-24 rounded-sm overflow-hidden bg-[#E6E1D8] border border-black/10 shrink-0">
                  {form.image ? <img src={form.image} alt="" className="w-full h-full object-cover" data-testid="pf-image-preview" /> : null}
                </div>
                <label className="inline-flex items-center gap-2 px-5 py-3 border border-black/15 rounded-sm text-xs tracking-widest uppercase cursor-pointer hover:bg-[#0E0F12] hover:text-[#EDEAE3] transition-colors">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.5} />}
                  {uploading ? "Se încarcă…" : "Încarcă imagine"}
                  <input data-testid="pf-image-input" type="file" accept="image/*" onChange={onFile} className="hidden" />
                </label>
              </div>
            </div>

            <div className="flex items-center gap-6 pt-2">
              <label className="inline-flex items-center gap-3 cursor-pointer">
                <button type="button" data-testid="pf-bestseller" onClick={() => toggle("bestseller")}
                  className={`w-11 h-6 rounded-full transition-colors relative ${form.bestseller ? "bg-gramur" : "bg-black/20"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${form.bestseller ? "left-[22px]" : "left-0.5"}`} />
                </button>
                <span className="text-sm">Best seller</span>
              </label>
              <label className="inline-flex items-center gap-3 cursor-pointer">
                <button type="button" data-testid="pf-featured" onClick={() => toggle("featured")}
                  className={`w-11 h-6 rounded-full transition-colors relative ${form.featured ? "bg-gramur" : "bg-black/20"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${form.featured ? "left-[22px]" : "left-0.5"}`} />
                </button>
                <span className="text-sm">Recomandat</span>
              </label>
            </div>

            <div className="flex gap-3 pt-4">
              <button type="button" data-testid="admin-form-cancel" onClick={() => setFormOpen(false)} className="flex-1 py-3.5 border border-black/15 rounded-sm text-sm tracking-widest uppercase hover:bg-black/5 transition-colors">Anulează</button>
              <button type="submit" data-testid="admin-form-save" disabled={saving || uploading} className="flex-1 py-3.5 bg-[#0E0F12] text-[#EDEAE3] rounded-sm text-sm tracking-widest uppercase hover:-translate-y-[2px] transition-transform disabled:opacity-60">
                {saving ? "Se salvează…" : editing ? "Salvează" : "Adaugă"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <DialogContent className="max-w-md bg-[#EDEAE3] border border-black/10 rounded-md [&>button]:hidden" data-testid="delete-confirm">
          <DialogTitle className="font-serif text-2xl font-light">Ștergi produsul?</DialogTitle>
          <DialogDescription className="text-[#4A4844]">
            „{pendingDelete?.name}" va fi eliminat definitiv din magazin.
          </DialogDescription>
          <div className="flex gap-3 pt-4">
            <button onClick={() => setPendingDelete(null)} className="flex-1 py-3.5 border border-black/15 rounded-sm text-sm tracking-widest uppercase hover:bg-black/5 transition-colors">Anulează</button>
            <button data-testid="delete-confirm-yes" onClick={confirmDelete} className="flex-1 py-3.5 bg-red-600 text-white rounded-sm text-sm tracking-widest uppercase hover:-translate-y-[2px] transition-transform">Șterge</button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}

const Lbl = ({ children }) => <span className="block text-[11px] tracking-[0.2em] uppercase text-[#4A4844] mb-2">{children}</span>;

const STATUS_META = {
  noua: { label: "Nouă", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  primita: { label: "Nouă", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  confirmata: { label: "Confirmată", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  expediata: { label: "Expediată", cls: "bg-green-100 text-green-800 border-green-300" },
  anulata: { label: "Anulată", cls: "bg-red-100 text-red-700 border-red-300" },
  returnata: { label: "Returnată", cls: "bg-orange-100 text-orange-800 border-orange-300" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.noua;
  return (
    <span data-testid="order-status-badge" className={`inline-block px-2.5 py-1 rounded-full border text-[10px] tracking-wider uppercase ${m.cls}`}>
      {m.label}
    </span>
  );
}


function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="border border-black/10 rounded-md p-5 bg-white/40">
      <div className="flex items-center gap-2 text-[#4A4844] mb-3">
        <Icon className="w-4 h-4" strokeWidth={1.6} />
        <span className="text-[11px] tracking-[0.2em] uppercase">{label}</span>
      </div>
      <p className="font-serif text-3xl leading-none text-[#0E0F12]">{value}</p>
      {sub && <p className="text-xs text-[#4A4844] mt-2">{sub}</p>}
    </div>
  );
}


function Field({ label, testid, value, onChange, type = "text", step, required }) {
  return (
    <label className="block">
      <Lbl>{label}</Lbl>
      <input data-testid={testid} type={type} step={step} value={value} onChange={onChange} required={required}
        className="w-full bg-transparent border border-black/15 rounded-sm focus:border-gramur outline-none p-3 text-sm transition-colors" />
    </label>
  );
}
